import { Injectable, inject } from '@angular/core';

import { ScoringSession } from '@core/models/brijjsports.models';
import { BrijjSportsStateService } from '@core/state/brijjsports-state.service';

import { ScoringAdapter, SportDashboardTile } from '../sport.types';
import { Game } from './softball.models';

@Injectable({ providedIn: 'root' })
export class SoftballScoringAdapter implements ScoringAdapter {
  private readonly state = inject(BrijjSportsStateService);

  private gameForSession(sessionId: string): Game | undefined {
    return this.state.softballGames().find(g => g.id === sessionId);
  }

  athleteTotalInSession(session: ScoringSession, athleteId: string): number {
    const game = this.gameForSession(session.id);
    if (!game) return 0;
    const counts = this.state
      .softballPitchCounts()
      .filter(pc => pc.gameId === game.id && pc.pitcherId === athleteId);
    return counts.reduce((sum, pc) => sum + pc.pitches, 0);
  }

  athleteIdsInSession(session: ScoringSession): string[] {
    if (session.participantIds?.length) return session.participantIds;
    const game = this.gameForSession(session.id);
    if (!game) return [];
    const lineup = this.state.softballLineups().find(l => l.id === game.lineupId);
    if (!lineup) return [];
    return this.state
      .softballLineupSlots()
      .filter(s => s.lineupId === lineup.id)
      .map(s => s.athleteId);
  }

  sessionSortKey(session: ScoringSession): number {
    return new Date(session.startedAt ?? session.scheduledFor ?? session.date).getTime();
  }

  sessionLabel(session: ScoringSession): string {
    if (session.name) return session.name;
    const game = this.gameForSession(session.id);
    if (game?.opponentName) return `vs ${game.opponentName}`;
    return 'Softball game';
  }

  bestForAthlete(sessions: ScoringSession[], athleteId: string): number {
    return sessions
      .filter(s => s.sportKey === 'softball')
      .reduce((max, s) => Math.max(max, this.athleteTotalInSession(s, athleteId)), 0);
  }

  statTiles(session: ScoringSession): SportDashboardTile[] {
    const game = this.gameForSession(session.id);
    if (!game) return [];
    return [
      { label: 'Home', value: game.homeRuns },
      { label: 'Away', value: game.awayRuns },
      { label: 'Inning', value: game.currentInning ?? game.scheduledInnings }
    ];
  }
}
